import React, { useState } from 'react';
import styled from 'styled-components';
import { useSaldosDashboard } from '../hooks/useSaldosDashboard';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { theme } from '../config/theme';
import DashboardIndicators from './saldos/DashboardIndicators';
import DeudoresAlertCard from './saldos/DeudoresAlertCard';
import ExposureCard from './saldos/ExposureCard';
import MovimientosDelDiaCard from './saldos/MovimientosDelDiaCard';
import MovimientosDetalleModal from './saldos/MovimientosDetalleModal';
import TopClientesCard from './saldos/TopClientesCard';
import ProximosVencimientosSection from './saldos/ProximosVencimientosSection';
import SaldoDesgloseModal from './SaldoDesgloseModal';
import { formatSnapshotHora } from '../services/saldosService';
import { BUCKET_ARS, BUCKET_USD, BUCKET_CABLE, MONEDA_TITULO } from '../constants/monedas';

// Tres columnas de moneda necesitan más ancho que las dos de antes.
const STACK_BREAKPOINT = '1500px';

const TabWrapper = styled.div`
  font-family: ${props => props.theme.typography.fontFamily};
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 2rem;
`;
const HeaderBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;
const Title = styled.h2`
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 2rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
`;
const SnapshotBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: rgba(26, 40, 62, 0.08);
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.secondary};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.5rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 500;
  strong { text-transform: capitalize; color: ${props => props.theme.colors.primary}; }
`;
/*
 * Grilla por filas compartidas: la terna de celdas de una misma fila (Pesos |
 * Dólares | USD Cable) ocupa la misma fila, así los bloques homólogos quedan
 * alineados horizontalmente entre monedas. En pantallas angostas colapsa a una
 * columna y el prop $order reagrupa todas las celdas de una moneda juntas.
 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$columnas}, minmax(0, 1fr));
  column-gap: 2rem;
  row-gap: 1.5rem;
  align-items: stretch;
  @media (max-width: ${STACK_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;
const Cell = styled.div`
  min-width: 0;
  @media (max-width: ${STACK_BREAKPOINT}) {
    order: ${props => props.$order};
  }
`;
const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.secondary};
  padding-bottom: 0.6rem;
  border-bottom: 2px solid ${props => props.theme.colors.primary};
`;
const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 1rem;
  align-items: stretch;
  height: 100%;
`;
const Placeholder = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  color: ${props => props.theme.colors.textSecondary};
`;
const PlaceholderLink = styled.button`
  display: block;
  margin: 1rem auto 0;
  font-family: ${props => props.theme.typography.fontFamily};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.textPrimary};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.6rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background-color: #333; }
`;

/* Celular: una moneda a la vez, elegida con un selector segmentado. */
const MonedaSwitch = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$opciones}, minmax(0, 1fr));
  gap: 0.25rem;
  padding: 0.25rem;
  margin-bottom: 1.25rem;
  background-color: rgba(26, 40, 62, 0.08);
  border-radius: 10px;
`;
const MonedaOption = styled.button`
  position: relative;
  min-height: 42px;
  border: none;
  border-radius: 8px;
  font-family: ${props => props.theme.typography.titleFamily};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  background-color: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? props.theme.colors.textPrimary : props.theme.colors.secondary};
  transition: background-color 0.15s, color 0.15s;
`;
const AlertDot = styled.span`
  position: absolute;
  top: 6px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.errorText};
`;

const SOLID_GREEN = theme.colors.success.replace('0.15', '1');

const CATEGORIA_LABELS = {
  cauciones: 'Cauciones ejecutadas del día',
  pases: 'Pases ejecutados del día',
  operacionesCI: 'Operaciones C.I. ejecutadas del día',
  ingresosEgresos: 'Ingresos y egresos ejecutados del día',
  fci: 'Movimientos FCI ejecutados del día',
  licitaciones: 'Licitaciones adjudicadas en día de caja',
  total: 'Movimientos ejecutados del día',
};

const ejecutadosItems = (desglose) => [
  ...desglose.cauciones.items,
  ...(desglose.pases?.items || []),
  ...desglose.operacionesCI.items,
  ...desglose.ingresosEgresos.items,
  ...desglose.fci.items,
  ...(desglose.licitaciones?.items || []),
];

/*
 * `alcance` acota el tablero a las cuentas de una persona:
 *   null                          → toda la casa (Trader, Middle Office, Admin, y el
 *                                   comercial que no figura en el padrón del SGC).
 *   { comitentes, isLoading }     → solo esas cuentas (rol Comercial del padrón).
 * El recorte es de vista: un comercial es staff y las reglas lo dejan leer todo,
 * así que el alcance sale del padrón del SGC (ver resolveAlcanceDashboard).
 *
 * `mostrarVencimientos` suma la sección de próximos vencimientos con el mismo
 * alcance (el productor la ve siempre).
 */
const DashboardTab = ({ onPrecargarCaucion, onNavigateToSaldos, alcance = null, mostrarVencimientos = false }) => {
  const { ars, usd, cable, snapshotActivo, hasData, isLoading, esModoProductor, hayRecorte } =
    useSaldosDashboard(alcance ? alcance.comitentes : null);
  const [desgloseModal, setDesgloseModal] = useState({ isOpen: false, cuenta: null, moneda: BUCKET_ARS });
  const [detalleModal, setDetalleModal] = useState({ isOpen: false, titulo: '', moneda: BUCKET_ARS, items: [] });
  const isMobile = useMediaQuery();
  const [monedaMobile, setMonedaMobile] = useState(BUCKET_ARS);

  const openDesglose = (moneda) => (cuenta) =>
    setDesgloseModal({ isOpen: true, cuenta, moneda });

  const openEjecutados = (moneda, data) => () => setDetalleModal({
    isOpen: true,
    titulo: CATEGORIA_LABELS.total,
    moneda,
    items: ejecutadosItems(data.desglose),
  });

  const openPendientes = (moneda, data) => () => setDetalleModal({
    isOpen: true,
    titulo: 'Movimientos pendientes de ejecución',
    moneda,
    items: data.pendientes.items,
  });

  const openCategoria = (moneda, data) => (key) => setDetalleModal({
    isOpen: true,
    titulo: CATEGORIA_LABELS[key],
    moneda,
    items: key === 'total' ? ejecutadosItems(data.desglose) : (data.desglose[key]?.items || []),
  });

  const columnas = [
    { bucket: BUCKET_ARS, data: ars },
    { bucket: BUCKET_USD, data: usd },
    { bucket: BUCKET_CABLE, data: cable },
  ];

  const hayAlertas = columnas.some(c => c.data.deudoresSinPrecarga.length > 0);
  // En el celular las tres monedas apiladas eran varias pantallas de scroll.
  const columnasVisibles = isMobile ? columnas.filter(c => c.bucket === monedaMobile) : columnas;

  const renderAlert = (moneda, data) => data.deudoresSinPrecarga.length > 0 && (
    <DeudoresAlertCard
      moneda={moneda}
      deudores={data.deudoresSinPrecarga}
      onOpenDesglose={openDesglose(moneda)}
      onPrecargarCaucion={onPrecargarCaucion}
    />
  );

  const renderCharts = (moneda, data) => (
    <SplitGrid>
      <ExposureCard moneda={moneda} totalAcreedor={data.totalAcreedor} totalDeudor={data.totalDeudor} />
      <MovimientosDelDiaCard
        desglose={data.desglose}
        currency={moneda}
        onSelectCategoria={openCategoria(moneda, data)}
      />
    </SplitGrid>
  );

  const renderTops = (moneda, data) => (
    <SplitGrid>
      <TopClientesCard
        titulo="Top 5 acreedores (saldo real)"
        entries={data.topAcreedores}
        currency={moneda}
        color={SOLID_GREEN}
        emptyText="Sin saldos acreedores."
        onSelect={openDesglose(moneda)}
      />
      <TopClientesCard
        titulo="Top 5 deudores (saldo real)"
        entries={data.topDeudores}
        currency={moneda}
        color={theme.colors.errorText}
        emptyText="Sin saldos deudores."
        onSelect={openDesglose(moneda)}
      />
    </SplitGrid>
  );

  /*
   * Una fila por bloque del tablero. Se renderizan fila por fila y columna por
   * columna para que la grilla alinee los bloques homólogos entre monedas.
   */
  const filas = [
    ...(isMobile ? [] : [{ key: 'titulo', render: ({ bucket }) => <SectionTitle>{MONEDA_TITULO[bucket]}</SectionTitle> }]),
    ...(hayAlertas ? [{ key: 'alerta', render: ({ bucket, data }) => renderAlert(bucket, data) }] : []),
    {
      key: 'indicadores',
      render: ({ bucket, data }) => (
        <DashboardIndicators
          moneda={bucket}
          data={data}
          onShowEjecutados={openEjecutados(bucket, data)}
          onShowPendientes={openPendientes(bucket, data)}
        />
      ),
    },
    { key: 'graficos', render: ({ bucket, data }) => renderCharts(bucket, data) },
    { key: 'tops', render: ({ bucket, data }) => renderTops(bucket, data) },
  ];

  // El comercial figura en el padrón pero sin cuentas: son cero clientes, no un
  // alcance indeterminado, así que no se cae al dashboard general.
  const sinCuentasAsignadas = !!alcance && !alcance.isLoading && alcance.comitentes.length === 0;
  // Sin cuentas asignadas el placeholder ya lo dice; mientras carga el alcance no se
  // sabe qué cuentas mostrar.
  const vencimientosVisibles = esModoProductor
    || (mostrarVencimientos && !alcance?.isLoading && !sinCuentasAsignadas);

  const renderPlaceholder = () => {
    if (alcance?.isLoading) return <Placeholder>Cargando tus clientes...</Placeholder>;
    if (sinCuentasAsignadas) {
      return (
        <Placeholder>
          Todavía no tenés clientes asignados en el Sistema de Gestión Comercial, así que el
          dashboard no tiene cuentas para mostrar. Una vez que te los asignen aparecen acá en
          la sincronización siguiente.
        </Placeholder>
      );
    }
    if (isLoading) return <Placeholder>Cargando datos...</Placeholder>;
    if (!hasData) {
      return (
        <Placeholder>
          {esModoProductor
            ? 'Todavía no hay saldos cargados para hoy. Los vas a ver acá en cuanto Axon Capital suba el archivo del custodio.'
            : hayRecorte
              ? 'No hay saldos cargados para hoy en las cuentas de tus clientes.'
              : 'No hay datos de saldos cargados para hoy.'}
          {!esModoProductor && (
            <PlaceholderLink onClick={onNavigateToSaldos}>
              Ir a Control de Saldos
            </PlaceholderLink>
          )}
        </Placeholder>
      );
    }
    return null;
  };

  return (
    <TabWrapper>
      <Header>
        <Title className="axon-page-title">Dashboard de Saldos</Title>
        <HeaderBadges>
          {alcance && !alcance.isLoading && (
            <SnapshotBadge title="El dashboard agrega solo los clientes que tenés asignados en el Sistema de Gestión Comercial.">
              Solo tus clientes: <strong>{alcance.comitentes.length}</strong>
              {alcance.comitentes.length === 1 ? ' cuenta' : ' cuentas'}
            </SnapshotBadge>
          )}
          {snapshotActivo && (
            <SnapshotBadge>
              Snapshot activo: <strong>{snapshotActivo.tipo}</strong> @ {formatSnapshotHora(snapshotActivo.uploadedAt)}
            </SnapshotBadge>
          )}
        </HeaderBadges>
      </Header>

      {renderPlaceholder() || (
        <>
        {isMobile && (
          <MonedaSwitch role="tablist" aria-label="Moneda" $opciones={columnas.length}>
            {columnas.map(({ bucket, data }) => (
              <MonedaOption
                key={bucket}
                type="button"
                role="tab"
                aria-selected={monedaMobile === bucket}
                $active={monedaMobile === bucket}
                onClick={() => setMonedaMobile(bucket)}
              >
                {MONEDA_TITULO[bucket]}
                {data.deudoresSinPrecarga.length > 0 && (
                  <AlertDot aria-label="Hay deudores sin caución precargada" />
                )}
              </MonedaOption>
            ))}
          </MonedaSwitch>
        )}
        <Grid $columnas={columnasVisibles.length}>
          {filas.map((fila, filaIndex) => columnasVisibles.map((columna, columnaIndex) => (
            <Cell
              key={`${fila.key}-${columna.bucket}`}
              $order={columnaIndex * filas.length + filaIndex}
            >
              {fila.render(columna)}
            </Cell>
          )))}
        </Grid>
        </>
      )}

      {/* Los vencimientos no dependen del snapshot del custodio: van aunque no haya saldos de hoy. */}
      {vencimientosVisibles && <ProximosVencimientosSection alcance={alcance ? alcance.comitentes : null} />}

      <SaldoDesgloseModal
        isOpen={desgloseModal.isOpen}
        onClose={() => setDesgloseModal({ isOpen: false, cuenta: null, moneda: BUCKET_ARS })}
        cuenta={desgloseModal.cuenta}
        moneda={desgloseModal.moneda}
        snapshotInfo={snapshotActivo}
      />

      <MovimientosDetalleModal
        isOpen={detalleModal.isOpen}
        onClose={() => setDetalleModal({ isOpen: false, titulo: '', moneda: BUCKET_ARS, items: [] })}
        titulo={detalleModal.titulo}
        moneda={detalleModal.moneda}
        items={detalleModal.items}
      />
    </TabWrapper>
  );
};

export default DashboardTab;
